import _ from 'lodash';
import { createSelector } from 'reselect';

import * as Account from 'app/entities/Account';
import * as Plan from 'app/entities/Plan';
import * as Profile from 'app/entities/Profile';
import ServiceUtils from 'app/service/ServiceUtils';
import { FOLDER_ID_PARAM } from 'app/dashboard/utils/constants';
import { HeldReasons } from 'app/history/utils/constants';
import { authStub } from 'app/auth/utils/constants';
import { emptyObject } from 'app/common/CommonUtils';
import { getParam } from 'app/common/utils/RouterSelectors';
import { isZapInFolder } from 'app/dashboard/utils/DashboardUtils';
import { selectFlow } from 'app/beta-editor/Selectors';
import {
  getAuthApi,
  getAuthData,
  getAuths,
  isAuthDataShared,
  isAuthDataStale,
  selectIsStaleAuth,
} from 'app/auth/Selectors';
import {
  hasSufficientlyUpgraded,
  isPlanLimitHeldReason,
} from 'app/history/utils/TaskUtils';

export const selectHeldReasons = state =>
  _.get(state, ['dashboard', 'heldReasons'], emptyObject);

export const selectHeldReasonsRoots = state =>
  _.get(state, ['dashboard', 'heldReasons', 'roots'], emptyObject);

export const selectAreHeldReasonsLoading = state =>
  _.get(state, ['dashboard', 'heldReasons', 'isLoading'], false);

export const selectAreHeldReasonsLoaded = state =>
  _.get(state, ['dashboard', 'heldReasons', 'isLoaded'], false);

export const selectHeldTasksCount = state => selectHeldReasons(state).total;

export const selectZapIdsWithHeldTasks = createSelector(
  selectHeldReasonsRoots,
  roots => Object.keys(roots).map(Number)
);

export const selectIsHeldTasksBannerOpen = state =>
  _.get(state, ['dashboard', 'heldTasksBanner', 'isOpen']);

export const selectHeldTasksSnapshot = state =>
  _.get(state, ['dashboard', 'heldTasksBanner', 'snapshot']);

export const getFolderIdParam = state => {
  return getParam(FOLDER_ID_PARAM, state);
};

export const getFlows = state => _.get(state, ['flow', 'flows']);

// Apparently some zaps use `auth_id` and some use `authentication_id`.
// Maybe a legacy thing?
const getNodeAuthId = node => {
  return _.get(node, 'auth_id') || _.get(node, 'authentication_id');
};

const getAuthIdsForZap = (zapId, state) => {
  const zap = selectFlow(zapId, state) || {};
  const nodes = zap.nodes || {};
  return _.uniq(
    Object.keys(nodes)
      .map(nodeId => getNodeAuthId(nodes[nodeId]))
      .filter(Boolean)
  );
};

export const isZapOwned = (zapOwnerId, state) => {
  const profileId = Profile.selectors.all.currentProfileId(state);

  if (zapOwnerId === undefined && profileId === undefined) return false;
  return zapOwnerId === profileId;
};

/**
 * Returns a list of the services in use by the currently loaded flows
 */
export const getInUseServices = createSelector([getFlows], flows =>
  _.reduce(
    flows,
    (acc, flow) => {
      const nodes = _.get(flow, 'nodes', {});

      return _.reduce(
        nodes,
        (accum, node) => {
          const selectedApi = _.get(node, 'selected_api', undefined);

          if (selectedApi && !_.includes(accum, selectedApi)) {
            accum.push(selectedApi);
          }

          return accum;
        },
        acc
      );
    },
    []
  )
);

export const getAuthsForZap = (zapId, state) => {
  return getAuthIdsForZap(zapId, state).map(
    authId => getAuthData(authId, state) || authStub
  );
};

export const getStaleAuthServiceNamesByZap = (zapId, services, state) => {
  const authsForZap = getAuthsForZap(zapId, state);

  return authsForZap.reduce((acc, auth) => {
    if (selectIsStaleAuth(auth.id, state)) {
      const serviceName = ServiceUtils.nameFromApi(auth.selected_api, services);
      return [...acc, serviceName];
    }

    return acc;
  }, []);
};

export const getPrivateAuthsForZap = (zapId, state) => {
  return getAuthsForZap(zapId, state).filter(_.negate(isAuthDataShared));
};

export const getZapsInFolder = (folderId, state) => {
  return _.values(getFlows(state))
    .filter(Boolean)
    .filter(_.partial(isZapInFolder, folderId));
};

const getNoLongerStaleCount = (reason, auths, heldData) => {
  if (reason !== HeldReasons.STALE_AUTH || !auths) {
    return 0;
  }

  return _.reduce(
    heldData.ids,
    (sum, count, id) => {
      const auth = _.get(auths, id, {});
      const noLongerStaleCount = !isAuthDataStale(auth) ? count : 0;
      return sum + noLongerStaleCount;
    },
    0
  );
};

const getHasSufficientlyUpgradedCount = (reason, currentPlan, heldData) => {
  const limits = _.toPairs(_.omit(heldData.limits, 'task_limit'));

  return _.reduce(
    limits,
    (count, limit) => {
      const [heldPlanValue, heldCount] = limit;
      if (hasSufficientlyUpgraded(reason, heldPlanValue, currentPlan)) {
        return count + heldCount;
      }
      return 0;
    },
    0
  );
};

const normalizeReason = reason =>
  isPlanLimitHeldReason(reason) ? HeldReasons.PLAN_LIMIT : reason;

const getCountsByHeldReason = (
  roots,
  auths,
  currentPlan,
  currentAccount,
  noAdjustments
) =>
  _.reduce(
    roots,
    (counts, data) => {
      return _.reduce(
        data.reasons,
        (newCounts, heldData, reason) => {
          const normalizedReason = normalizeReason(reason);
          const noLongerStaleCount = noAdjustments
            ? 0
            : getNoLongerStaleCount(normalizedReason, auths, heldData);
          const hasSufficientlyUpgradedCount = noAdjustments
            ? 0
            : getHasSufficientlyUpgradedCount(reason, currentPlan, heldData);

          const currentCount =
            (newCounts[normalizedReason] || 0) +
            (heldData.total || 0) -
            hasSufficientlyUpgradedCount -
            noLongerStaleCount;

          const isPaymentIssue = !!heldData.payment_issue_total;
          const isPaymentIssueResolved =
            isPaymentIssue && !Account.selectors.lastChargeFail(currentAccount);

          const currentPaymentIssueCount =
            ((!isPaymentIssueResolved || noAdjustments) &&
              heldData.payment_issue_total) ||
            0;

          const newCount = currentCount
            ? currentCount - currentPaymentIssueCount
            : 0;

          const paymentIssueCount =
            (newCounts.paymentIssue || 0) + currentPaymentIssueCount;

          return {
            ...newCounts,
            [normalizedReason]: newCount,
            paymentIssue: paymentIssueCount,
          };
        },
        counts
      );
    },
    {}
  );

// Aggregated
export const selectCountsByHeldReason = createSelector(
  selectHeldReasons,
  getAuths,
  Plan.selectors.all.currentPlan,
  Account.selectors.all.currentAccount,
  (state, { noAdjustments } = {}) => noAdjustments,
  (heldReasons, auths, currentPlan, currentAccount, noAdjustments) =>
    getCountsByHeldReason(
      heldReasons.roots,
      auths,
      currentPlan,
      currentAccount,
      noAdjustments
    )
);

// Aggregated: Takes the earliest starts_expiring_on date out of all the Zaps with held tasks
export const selectHeldTasksStartsExpiringOn = createSelector(
  selectHeldReasons,
  heldReasons => heldReasons.starts_expiring_on
);

// Aggregated total held tasks
export const selectTotalHeldTasks = createSelector(
  selectHeldReasons,
  heldReasons => heldReasons.total
);

export const selectCountsByHeldReasonByZapId = createSelector(
  selectHeldReasons,
  getAuths,
  Plan.selectors.all.currentPlan,
  Account.selectors.all.currentAccount,
  (state, zapId) => zapId,
  (heldReasons, auths, currentPlan, currentAccount, zapId) => {
    const heldReasonsByZapId = _.pick(heldReasons.roots, zapId);

    return getCountsByHeldReason(
      heldReasonsByZapId,
      auths,
      currentPlan,
      currentAccount
    );
  }
);

export const selectHeldTasksStartsExpiringOnByZapId = createSelector(
  selectHeldReasons,
  (state, zapId) => zapId,
  (heldReasons, zapId) => {
    const heldReasonsByZapId = _.pick(heldReasons.roots, zapId);
    return heldReasonsByZapId[zapId]
      ? heldReasonsByZapId[zapId].starts_expiring_on
      : undefined;
  }
);

export const selectAreAllStartsExpiringOnTheSameDay = createSelector(
  selectHeldReasonsRoots,
  roots => {
    const rootsArr = Object.keys(roots);
    return rootsArr.every(
      zapId =>
        roots[zapId].starts_expiring_on ===
        roots[rootsArr[0]].starts_expiring_on
    );
  }
);

export const selectHeldTasksTotalByZapId = createSelector(
  selectHeldReasons,
  (state, zapId) => zapId,
  (heldReasons, zapId) => {
    const heldReasonsByZapId = _.pick(heldReasons.roots, zapId);
    return heldReasonsByZapId[zapId] ? heldReasonsByZapId[zapId].total : 0;
  }
);

const filterNoLongerStaleIds = (staleIds, auths) =>
  _.filter(staleIds, id => auths && isAuthDataStale(auths[id]));

export const selectStaleAuthIds = createSelector(
  selectHeldReasons,
  getAuths,
  (heldReasons, auths) => {
    const staleIds = _.reduce(
      heldReasons.roots,
      (ids, data) =>
        ids.concat(
          Object.keys(
            _.get(data, ['reasons', HeldReasons.STALE_AUTH, 'ids'], [])
          )
        ),
      []
    );
    return _.uniq(filterNoLongerStaleIds(staleIds, auths));
  }
);

export const getAuthApis = (staleAuthIds, state) =>
  _.uniq(_.compact(staleAuthIds.map(id => getAuthApi(id, state))));

export const selectStaleAuthApis = state =>
  getAuthApis(selectStaleAuthIds(state), state);

export const selectHeldTaskStaleAuthServiceNames = (state, services) => {
  if (_.isEmpty(services)) {
    return [];
  }
  const staleAuthApis = selectStaleAuthApis(state);
  return staleAuthApis.map(api => ServiceUtils.nameFromApi(api, services));
};

export const selectStaleAuthIdsForZap = createSelector(
  selectHeldReasons,
  getAuths,
  (state, props) => props,
  (heldReasons, auths, zapId) => {
    const counts = _.get(
      heldReasons,
      ['roots', zapId, 'reasons', HeldReasons.STALE_AUTH, 'ids'],
      {}
    );
    const staleIds = Object.keys(counts);
    return filterNoLongerStaleIds(staleIds, auths);
  }
);
